import { useState, useEffect } from 'react';
import { Palette, Upload, Check, AlertCircle, Type } from '../../icons/wp-icons';
import { brandingApi } from '../../api/brandingApi';
import { loadBrandingSettings } from '../BrandingLoader';

const DEFAULT_COLORS = [
  { name: 'Blau', primary: '#378ab2', secondary: '#2a6d8a' },
  { name: 'Grün', primary: '#22c55e', secondary: '#16a34a' },
  { name: 'Rot', primary: '#ef4444', secondary: '#dc2626' },
  { name: 'Orange', primary: '#f97316', secondary: '#ea580c' },
  { name: 'Violett', primary: '#8b5cf6', secondary: '#7c3aed' },
  { name: 'Türkis', primary: '#14b8a6', secondary: '#0d9488' },
  { name: 'Grau', primary: '#6b7280', secondary: '#4b5563' },
  { name: 'Schwarz', primary: '#1f2937', secondary: '#111827' }
];

function BrandingStep({ data = {}, onDataChange }) {
  const [selectedColor, setSelectedColor] = useState(DEFAULT_COLORS[0]);
  const [customColor, setCustomColor] = useState('');
  const [selectedFont, setSelectedFont] = useState('Montserrat');
  const [availableFonts, setAvailableFonts] = useState([]);
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const loadBranding = async () => {
      try {
        const branding = await brandingApi.get();

        // Farbe setzen
        if (branding.accentColor) {
          const found = DEFAULT_COLORS.find(c => c.primary === branding.accentColor);
          if (found) {
            setSelectedColor(found);
          } else {
            setCustomColor(branding.accentColor);
            setSelectedColor(null);
          }
        }

        // Schriftart setzen
        if (branding.headingFont) {
          setSelectedFont(branding.headingFont);
        }

        // Verfügbare Schriftarten laden
        if (branding.availableFonts) {
          setAvailableFonts(branding.availableFonts);
        }

        // Logo setzen
        if (branding.logoPath) {
          setLogo(branding.logoPath);
        }
      } catch (err) {
        console.error('Fehler beim Laden des Brandings:', err);
      } finally {
        setLoading(false);
      }
    };
    loadBranding();

    // Immer valid, da optional
    onDataChange({}, true);
  }, []);

  const handleColorSelect = async (color) => {
    setSelectedColor(color);
    setCustomColor('');
    setSaving(true);
    try {
      await brandingApi.update({
        accentColor: color.primary
      });
      // Branding im Header aktualisieren
      await loadBrandingSettings();
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleCustomColorChange = async (e) => {
    const color = e.target.value;
    setCustomColor(color);
    setSelectedColor(null);

    if (color.match(/^#[0-9A-Fa-f]{6}$/)) {
      setSaving(true);
      try {
        await brandingApi.update({
          accentColor: color
        });
        // Branding im Header aktualisieren
        await loadBrandingSettings();
      } catch (err) {
        console.error('Fehler beim Speichern:', err);
      } finally {
        setSaving(false);
      }
    }
  };

  const handleFontSelect = async (fontName) => {
    setSelectedFont(fontName);
    setSaving(true);
    try {
      await brandingApi.update({
        headingFont: fontName
      });
      // Branding im Header aktualisieren
      await loadBrandingSettings();
    } catch (err) {
      console.error('Fehler beim Speichern:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setSaving(true);
    try {
      const result = await brandingApi.uploadLogo(file);
      if (result.logoPath) {
        setLogo(result.logoPath);
        // Branding im Header aktualisieren
        await loadBrandingSettings();
      }
    } catch (err) {
      console.error('Fehler beim Upload:', err);
      alert('Fehler beim Hochladen: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Ihr Branding
        </h3>
        <p className="text-gray-600 text-sm mb-6">
          Passen Sie das Design an Ihre Unternehmensfarben an.
        </p>
      </div>

      {/* Farbauswahl */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Primärfarbe
        </label>
        <div className="grid grid-cols-4 md:grid-cols-8 gap-3">
          {DEFAULT_COLORS.map((color) => (
            <button
              key={color.name}
              onClick={() => handleColorSelect(color)}
              className={`
                relative w-12 h-12 rounded-xl transition-transform hover:scale-110
                ${selectedColor?.name === color.name ? 'ring-2 ring-offset-2 ring-gray-900' : ''}
              `}
              style={{ backgroundColor: color.primary }}
              title={color.name}
            >
              {selectedColor?.name === color.name && (
                <Check className="w-5 h-5 text-white absolute inset-0 m-auto" />
              )}
            </button>
          ))}
        </div>

        {/* Custom Color */}
        <div className="mt-4 flex items-center gap-3">
          <label className="text-sm text-gray-600">Eigene Farbe:</label>
          <input
            type="color"
            value={customColor || '#378ab2'}
            onChange={handleCustomColorChange}
            className="w-10 h-10 rounded cursor-pointer border-0"
          />
          <input
            type="text"
            value={customColor}
            onChange={(e) => setCustomColor(e.target.value)}
            placeholder="#378ab2"
            className="w-28 px-3 py-1 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#378ab2]"
          />
        </div>
      </div>

      {/* Schriftart-Auswahl */}
      {availableFonts.length > 0 && (
        <div className="border-t pt-6">
          <label className="block text-sm font-medium text-gray-700 mb-3">
            <Type className="w-4 h-4 inline mr-2" />
            Überschriften-Schriftart
          </label>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {availableFonts.map((font) => (
              <button
                key={font.name}
                onClick={() => handleFontSelect(font.name)}
                className={`
                  p-3 rounded-lg border-2 text-left transition-all
                  ${selectedFont === font.name
                    ? 'border-[#378ab2] bg-[#378ab2]/5'
                    : 'border-gray-200 hover:border-gray-300'}
                `}
              >
                <span
                  className="text-lg font-semibold block"
                  style={{ fontFamily: `'${font.name}', ${font.category}` }}
                >
                  {font.name}
                </span>
                <span className="text-xs text-gray-500">{font.category}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Logo Upload */}
      <div className="border-t pt-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Logo (optional)
        </label>
        <div className="flex items-start gap-6">
          {/* Preview */}
          <div className="w-24 h-24 bg-gray-100 rounded-xl flex items-center justify-center overflow-hidden">
            {logo ? (
              <img src={logo} alt="Logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <Palette className="w-8 h-8 text-gray-400" />
            )}
          </div>

          {/* Upload */}
          <div>
            <label className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer transition">
              <Upload className="w-5 h-5" />
              <span>Logo hochladen</span>
              <input
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                className="hidden"
              />
            </label>
            <p className="text-xs text-gray-500 mt-2">
              PNG, JPG oder SVG, max. 2MB
            </p>
          </div>
        </div>
      </div>

      {/* Preview */}
      <div className="border-t pt-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          Vorschau
        </label>
        <div className="bg-gray-50 rounded-xl p-4">
          <div className="flex items-center gap-3 mb-4">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center"
              style={{ backgroundColor: customColor || selectedColor?.primary || '#378ab2' }}
            >
              {logo ? (
                <img src={logo} alt="Logo" className="w-8 h-8 object-contain" />
              ) : (
                <span className="text-white font-bold">W</span>
              )}
            </div>
            <span
              className="text-xl font-semibold"
              style={{ fontFamily: `'${selectedFont}', sans-serif` }}
            >
              Ihre Firma
            </span>
          </div>
          <button
            className="px-4 py-2 text-white rounded-lg"
            style={{ backgroundColor: customColor || selectedColor?.primary || '#378ab2' }}
          >
            Beispiel-Button
          </button>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
        <AlertCircle className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <strong>Optional:</strong> Sie können das Branding jederzeit in den Einstellungen anpassen.
        </div>
      </div>
    </div>
  );
}

export default BrandingStep;
